// Admin Service - API client for admin operations

class AdminService {
  constructor() {
    this.baseURL = `${process.env.REACT_APP_API_URL || 'https://api.daggpt.network'}/api/admin`;
    this.token = localStorage.getItem('admin_token');
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem('admin_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('admin_token');
  }

  getAuthHeaders() {
    return {
      'Content-Type': 'application/json',
      ...(this.token && { 'Authorization': `Bearer ${this.token}` })
    };
  }

  async login(email, password) {
    try {
      const response = await fetch(`${this.baseURL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      this.setToken(data.token);
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async logout() {
    this.clearToken();
  }

  isAuthenticated() {
    return !!this.token;
  }

  async getAllModels() {
    try {
      const response = await fetch(`${this.baseURL}/models`, {
        headers: this.getAuthHeaders()
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch models');
      }

      return data.models;
    } catch (error) {
      console.error('Error fetching models:', error);
      throw error;
    }
  }

  async getModel(id) {
    try {
      const response = await fetch(`${this.baseURL}/models/${id}`, {
        headers: this.getAuthHeaders()
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch model');
      }

      return data.model;
    } catch (error) {
      console.error('Error fetching model:', error);
      throw error;
    }
  }

  async createModel(modelData) {
    try {
      const response = await fetch(`${this.baseURL}/models`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(modelData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create model');
      }

      return data.model;
    } catch (error) {
      console.error('Error creating model:', error);
      throw error;
    }
  }

  async updateModel(id, modelData) {
    try {
      const response = await fetch(`${this.baseURL}/models/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(modelData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update model');
      }

      return data.model;
    } catch (error) {
      console.error('Error updating model:', error);
      throw error;
    }
  }

  async deleteModel(modelId) {
    try {
      const response = await fetch(`${this.baseURL}/models/${modelId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete model');
      }

      return response.json();
    } catch (error) {
      console.error('Error deleting model:', error);
      throw error;
    }
  }

  async getDeletedModels() {
    try {
      const response = await fetch(`${this.baseURL}/models/deleted`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch deleted models');
      }

      const data = await response.json();
      return data.models;
    } catch (error) {
      console.error('Error fetching deleted models:', error);
      throw error;
    }
  }

  async restoreModel(modelId) {
    try {
      const response = await fetch(`${this.baseURL}/models/${modelId}/restore`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to restore model');
      }

      return response.json();
    } catch (error) {
      console.error('Error restoring model:', error);
      throw error;
    }
  }

  async permanentDeleteModel(modelId) {
    try {
      const response = await fetch(`${this.baseURL}/models/${modelId}/permanent`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to permanently delete model');
      }

      return response.json();
    } catch (error) {
      console.error('Error permanently deleting model:', error);
      throw error;
    }
  }

  async testModel(modelConfig, testMessage = 'Hello, this is a test message.') {
    try {
      const response = await fetch(`${this.baseURL}/models/test`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ modelConfig, testMessage })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to test model');
      }

      return data;
    } catch (error) {
      console.error('Error testing model:', error);
      throw error;
    }
  }

  async validateModel(modelConfig) {
    try {
      const response = await fetch(`${this.baseURL}/models/validate`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ modelConfig })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to validate model');
      }

      return data;
    } catch (error) {
      console.error('Error validating model:', error);
      throw error;
    }
  }

  async reloadModels() {
    try {
      const response = await fetch(`${this.baseURL}/reload-models`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reload models');
      }

      return data;
    } catch (error) {
      console.error('Error reloading models:', error);
      throw error;
    }
  }

  async getStats() {
    try {
      const response = await fetch(`${this.baseURL}/stats`, {
        headers: this.getAuthHeaders()
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch stats');
      }

      return data.stats;
    } catch (error) {
      console.error('Error fetching stats:', error);
      throw error;
    }
  }
}

const adminService = new AdminService();
export default adminService;
